'use client'

import { useEffect, useState } from 'react'

import { InviteFounderPanel } from './InviteFounderPanel'
import { ManualEntryPanel } from './ManualEntryPanel'

// Compact toolbar above the Z2H Applications list: the invite and manual
// fast-track forms live behind icon buttons (tooltip on hover) and open in a
// modal, keeping the list itself front and center for the admins working it.

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(680px, 92vw)',
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: 12,
          background: 'var(--theme-bg, var(--theme-elevation-0))',
          border: '1px solid var(--theme-elevation-150)',
          padding: '8px 16px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 0',
          }}
        >
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--theme-elevation-500)',
              fontSize: 18,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function IconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: 8,
        border: '1px solid var(--theme-elevation-150)',
        background: 'var(--theme-elevation-50)',
        color: 'var(--theme-text)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

export function Z2hToolbar() {
  const [open, setOpen] = useState(null) // null | 'invite' | 'manual'

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
      }}
    >
      <IconButton
        label="Invite a founder — email them their personal application link"
        onClick={() => setOpen('invite')}
      >
        {/* envelope-plus */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="2" y="5" width="16" height="14" rx="2" />
          <path d="m2 8 8 5 8-5" />
          <path d="M19 8h4M21 6v4" />
        </svg>
      </IconButton>
      <IconButton
        label="Manual entry (fast-track) — skip the interview and bring a founder straight into the program"
        onClick={() => setOpen('manual')}
      >
        {/* lightning */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M13 2 4.09 12.35a1 1 0 0 0 .76 1.65H11l-1 8 8.91-10.35a1 1 0 0 0-.76-1.65H13l1-8z" />
        </svg>
      </IconButton>
      <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>
        Invite · Fast-track — interview progress and follow-ups live in the
        table columns below.
      </span>

      {open === 'invite' && (
        <Modal title="Invite a founder" onClose={() => setOpen(null)}>
          <InviteFounderPanel />
        </Modal>
      )}
      {open === 'manual' && (
        <Modal title="Manual entry (fast-track)" onClose={() => setOpen(null)}>
          <ManualEntryPanel />
        </Modal>
      )}
    </div>
  )
}
